import { useState } from "react";
import { Shield, KeyRound, Fingerprint, Loader2, CheckCircle2, AlertTriangle, Info, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * First-time onboarding for the Private Device Vault.
 * PIN setup is mandatory; biometric enrollment is offered but optional.
 * The PIN is the ground truth — biometric only wraps the derived key locally.
 */
export default function VaultOnboarding({ vault, onDone }) {
  const [step, setStep] = useState("intro");           // intro | pin | biometric | done
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (pin.length < 6) return toast.error("PIN must be at least 6 characters");
    if (pin !== confirm) return toast.error("PINs don't match");
    setBusy(true);
    try {
      await vault.bootstrap(pin, { hint: hint.trim() });
      toast.success("Vault created on this device");
      setStep(vault.biometricAvailable ? "biometric" : "done");
    } catch (e) { toast.error(e.message || "Could not create vault"); }
    setBusy(false);
  };

  const enroll = async () => {
    setBusy(true);
    try { await vault.enrollBio(); toast.success("Biometric unlock enabled"); setStep("done"); }
    catch (e) { toast.error(e.message || "Could not enroll biometric — you can still unlock with PIN"); }
    setBusy(false);
  };

  const inp = "mt-1 w-full border-2 border-emerald-200 focus:border-emerald-500 px-4 py-3 text-lg font-mono tracking-wider text-center rounded";

  if (step === "intro") return (
    <div className="max-w-md mx-auto p-6" data-testid="vault-onboarding-intro">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-3">
          <Shield className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="font-serif font-900 text-2xl">Set up your Private Vault</h1>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Health, emergency contacts and household inventory live <b>only on this device</b>,
          encrypted with AES-GCM. No servers, no cloud sync — you own the keys.
        </p>
      </div>

      <div className="space-y-3 mb-6">
        {[
          ["Encrypted with your PIN", "AES-GCM 256 · PBKDF2 200k iterations"],
          ["Optional biometric unlock", "Face ID / Touch ID / Windows Hello"],
          ["Encrypted backup file", "You control cross-device transfer"],
          ["Zero cloud exposure", "TravelerPulse never sees the plaintext"],
        ].map(([t, s]) => (
          <div key={t} className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <div><div className="text-sm font-700 text-slate-900">{t}</div><div className="text-xs text-slate-600">{s}</div></div>
          </div>
        ))}
      </div>

      <div className="p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900 flex items-start gap-2 mb-6" data-testid="vault-onboarding-warning">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span><b>Important:</b> If you forget your PIN, your vault data cannot be recovered. Consider writing your hint or exporting a backup after setup.</span>
      </div>

      <button onClick={() => setStep("pin")} data-testid="vault-onboarding-continue"
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-700 rounded inline-flex items-center justify-center gap-2">
        Continue <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );

  if (step === "pin") return (
    <div className="max-w-md mx-auto p-6" data-testid="vault-onboarding-pin">
      <div className="text-center mb-6">
        <KeyRound className="w-10 h-10 mx-auto text-emerald-600" />
        <h2 className="font-serif font-900 text-xl mt-2">Choose your PIN or passphrase</h2>
        <p className="text-xs text-slate-600 mt-1">6+ characters. Longer is stronger — a memorable phrase beats a short PIN.</p>
      </div>
      <label className="block text-sm">
        <span className="font-600">PIN or passphrase</span>
        <input autoFocus type="password" value={pin} onChange={(e) => setPin(e.target.value)}
          className={inp} placeholder="••••••" data-testid="vault-onboarding-pin-input" />
      </label>
      <label className="block text-sm mt-3">
        <span className="font-600">Confirm</span>
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className={inp} placeholder="••••••" data-testid="vault-onboarding-pin-confirm" />
      </label>
      <label className="block text-sm mt-3">
        <span className="font-600">Memory hint <span className="text-slate-400 font-400">(optional, plaintext)</span></span>
        <input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={80}
          className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm rounded"
          placeholder="e.g. Where I proposed"
          data-testid="vault-onboarding-hint" />
      </label>
      <div className="flex gap-2 mt-6">
        <button onClick={() => setStep("intro")} className="flex-1 py-3 border border-slate-300 rounded font-700 text-sm">Back</button>
        <button onClick={create} disabled={busy || pin.length < 6 || pin !== confirm} data-testid="vault-onboarding-create"
          className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-700 rounded inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create vault
        </button>
      </div>
    </div>
  );

  if (step === "biometric") return (
    <div className="max-w-md mx-auto p-6" data-testid="vault-onboarding-bio">
      <div className="text-center mb-6">
        <Fingerprint className="w-10 h-10 mx-auto text-indigo-600" />
        <h2 className="font-serif font-900 text-xl mt-2">Enable biometric unlock?</h2>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          Use Face ID / Touch ID / Windows Hello to unlock this vault on this device.
          Your PIN still works as the fallback and remains the ground truth.
        </p>
      </div>
      <div className="p-3 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-900 mb-6 flex items-start gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Biometric enrollment is device-specific. If you use another device, you'll enroll again there.</span>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setStep("done")} data-testid="vault-onboarding-skip-bio"
          className="flex-1 py-3 border border-slate-300 rounded font-700 text-sm">Skip for now</button>
        <button onClick={enroll} disabled={busy} data-testid="vault-onboarding-enroll-bio"
          className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-700 rounded inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Enable biometric
        </button>
      </div>
    </div>
  );

  // done
  return (
    <div className="max-w-md mx-auto p-6 text-center" data-testid="vault-onboarding-done">
      <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
      <h2 className="font-serif font-900 text-2xl mt-3">You're all set</h2>
      <p className="text-sm text-slate-600 mt-2">Your Private Vault is ready. It's already unlocked for this session.</p>
      <button onClick={onDone} data-testid="vault-onboarding-open"
        className="mt-6 w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-700 rounded">
        Open my vault
      </button>
    </div>
  );
}
